import { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";

type RoutePatternId =
  | "pmv-step"
  | "network-step"
  | "controls-control"
  | "risks-risk"
  | "risks-risk-source";

/*
  RoutePattern is used to define the general structure of a route that we want to keep track of in recent routes.
    - pathname: 
      The path of the route. This supports params, optional segments, and wildcards:
        - Params: "/user/:id"
        - Optional segments: "/user/:id?"
        - Wildcards: "/user/*"
    - searchParamKeys:
      An array of keys for the search parameters that must be present for the route to be considered a match.
*/
type RoutePattern = {
  id: RoutePatternId;
  pathName: string;
  searchParamKeys?: string[];
};

const ROUTE_PATTERNS: RoutePattern[] = [
  {
    id: "pmv-step",
    pathName: "/steps/process-management/*",
    searchParamKeys: ["stepId"],
  },
  {
    id: "network-step",
    pathName: "/steps/network/detail/*",
    searchParamKeys: [],
  },
  {
    id: "controls-control",
    pathName: "/controls/:controlId",
    searchParamKeys: [],
  },
  {
    id: "risks-risk",
    pathName: "/risks",
    searchParamKeys: ["riskId"],
  },
  {
    id: "risks-risk-source",
    pathName: "/risks",
    searchParamKeys: ["riskSourceId"],
  },
];

// shape of each entry stored under "recentRoutes" in local storage
export type RecentRoute = {
  id: RoutePatternId;
  fullPath: string;
  searchParams: Record<string, string>;
  timestamp: number;
};

const MAX_RECENT_ROUTES = 5;

// listens for route changes and keeps track of recent routes
export function RecentRoutesProvider({ children }) {
  const { pathname, search } = useLocation();

  // runs only after a committed navigation, so re-renders don't bump recency
  useEffect(() => {
    const searchParams = new URLSearchParams(search);

    // gets the first matching pattern
    // only continue if the current route matches a pattern
    const matchingPattern = ROUTE_PATTERNS.find((pattern) =>
      routeMatchesPattern(pathname, searchParams, pattern),
    );
    if (!matchingPattern) {
      return;
    }

    // working list, starting from what is in local storage
    const fullPath = `${pathname}${search}`;
    let currentRoutes: RecentRoute[] = JSON.parse(
      localStorage.getItem("recentRoutes") || "[]",
    );

    // add the current route as the most recent entry
    currentRoutes = [
      {
        fullPath: fullPath,
        id: matchingPattern.id,
        searchParams: Object.fromEntries(searchParams),
        timestamp: Date.now(),
      },
      ...currentRoutes,
    ];

    // drop any older entries for the route that was just added
    currentRoutes = currentRoutes.filter(
      (route, index) => index === 0 || route.fullPath !== fullPath,
    );

    // trim to the max size and persist
    localStorage.setItem(
      "recentRoutes",
      JSON.stringify(currentRoutes.slice(0, MAX_RECENT_ROUTES)),
    );
  }, [pathname, search]);

  return children;
}

// hook that returns the recent routes from local storage
// can also be used for formatting individual routes
export default function useRecentRoutes() {
  function getRecentRoutes(): RecentRoute[] {
    return JSON.parse(localStorage.getItem("recentRoutes") || "[]");
  }

  return {
    getRecentRoutes,
  };
}

function routeMatchesPattern(
  pathName: string,
  searchParams: URLSearchParams,
  routePattern: RoutePattern,
): boolean {
  //wrapped the matchPath and searchParams checks in functions to allow for lazy evaluation
  const matchesPathName = () => !!matchPath(routePattern.pathName, pathName);

  const matchesSearchParams = () =>
    routePattern.searchParamKeys?.every((key) => searchParams.has(key)) ?? true;

  return matchesPathName() && matchesSearchParams();
}
